import { z } from 'zod';
/**
 * Lớp Constraints của harness — APM-H-c, `docs/plan-harness.md` §7.
 *
 * Bốn nguồn khai (mặc định trong code → chính sách tổ chức ở `system_setting`
 * → `skill.constraints` → chế độ chạy) gộp thành **một** `ResolvedConstraints`
 * cho mỗi run, ghi nguyên vào bước `policy` để người debug thấy run đó chạy
 * dưới luật nào và mỗi con số tới từ đâu.
 *
 * Mọi số ở đây đều là **trần**: skill chỉ được xin thấp hơn hoặc bằng trần tổ
 * chức, xin cao hơn thì bị kẹp và ghi `clamped`.
 */
export const AiRunLimits = z.object({
    /** Số lượt gọi model tối đa trong một run, kể cả lượt sửa output. */
    maxTurns: z.number().int().min(1).max(20),
    /** Số tool model được gọi song song trong một lượt. */
    maxToolCallsPerTurn: z.number().int().min(1).max(32),
    /** Một tool chạy quá lâu thì bị cắt và báo cho model. */
    toolTimeoutMs: z.number().int().min(1_000).max(120_000),
    /** Tổng token vào (input + cache read) của cả run. */
    maxInputTokens: z.number().int().min(1_000),
    /** Tổng chi phí một run, USD. */
    maxCostUsd: z.number().min(0.001),
    /** Số lần kết quả cuối được phép hỏng ở Verify trước khi kết thúc lỗi. */
    verifyRetries: z.number().int().min(0).max(5),
    /** Kết quả tool dài hơn bấy nhiêu token thì bị cắt có cấu trúc. */
    contextBudgetTokens: z.number().int().min(200),
    /** Gọi cùng tool cùng tham số quá bấy nhiêu lần trong một run ⇒ trần `loop` — APM-H-o. */
    maxIdenticalToolCalls: z.number().int().min(1).max(50),
    /** Gọi một tool (bất kể tham số) quá bấy nhiêu lần trong một run ⇒ trần `loop`. */
    maxCallsPerTool: z.number().int().min(1).max(200),
});
export const AiRunLimitKey = AiRunLimits.keyof();
/** Phần skill được khai trong code — mọi trường tuỳ chọn, bị kẹp trong trần tổ chức. */
/**
 * Mức suy nghĩ — APM-H-l (`plan-harness.md` §8). Một chiều **độc lập với tầng
 * model**: tầng nói model nào, reasoning nói nghĩ bao sâu. Thứ tự tăng dần dùng
 * để kẹp trần: `off < low < medium < high < adaptive`.
 */
export const AiReasoning = z.enum(['off', 'low', 'medium', 'high', 'adaptive']);
export const REASONING_ORDER = ['off', 'low', 'medium', 'high', 'adaptive'];
/** Mặc định theo tầng khi skill không khai — §8.2. */
export const REASONING_DEFAULT_BY_TIER = {
    strong: 'adaptive',
    standard: 'medium',
    small: 'off',
};
/** Reasoning đã gộp cho một run: xin gì, được gì, vì sao. `source = clamped` khi bị trần tổ chức kẹp. */
export const ResolvedReasoning = z.object({
    requested: AiReasoning,
    applied: AiReasoning,
    source: z.enum(['default', 'org', 'skill', 'clamped']),
});
/**
 * Agent phản biện — APM-CRIT-b (`docs/plan-critic-agent.md` §4).
 *
 * `CritiqueTier`: tầng model của người phản biện. `small` là mặc định (bài
 * "đối chiếu theo checklist", không phải bài suy luận sâu); `same` = cùng tầng
 * với skill — đắt nhất, chỉ khi bench cho thấy `small` bỏ sót. Thứ tự
 * `CRITIQUE_TIER_ORDER` là thứ tự chi phí: skill chỉ được xin **thấp hơn** trần tổ chức.
 */
export const CritiqueTier = z.enum(['small', 'standard', 'same']);
export const CRITIQUE_TIER_ORDER = ['small', 'standard', 'same'];
export const ResolvedCritique = z.object({
    /** Có gọi phản biện ở run này không: tổ chức bật, skill khai `critique`, skill nằm trong danh sách. */
    enabled: z.boolean(),
    /** Số vòng tối đa (0 = tắt). Vòng phản biện **không** trừ `verifyRetries`. */
    rounds: z.number().int().min(0).max(3),
    tier: CritiqueTier,
    /** Ưu tiên model khác họ với model vừa lập kết quả (giảm self-preference). */
    otherFamily: z.boolean(),
    /** Trần chi phí của **riêng** phản biện trong một run (USD); vượt ⇒ dừng vòng, chấp nhận output. */
    maxCostUsd: z.number().min(0),
    /** Trần bản chiếu `view()` đưa cho phản biện (token). */
    contextTokens: z.number().int().min(200),
    source: z.enum(['default', 'org', 'skill', 'clamped']),
    /** Vì sao `enabled = false` — để bước `policy` nói được lý do thay vì im lặng. */
    disabledReason: z
        .enum(['org_off', 'skill_not_declared', 'skill_off', 'skill_not_listed'])
        .optional(),
});
export const SkillConstraints = AiRunLimits.partial().extend({
    /** Mức suy nghĩ skill xin theo việc (`summarize: low`, `what_if: high`). Chỉ được ≤ trần tổ chức theo tầng. */
    reasoning: AiReasoning.optional(),
});
/**
 * Mỗi con số trong `limits` tới từ đâu.
 *
 * - `default`: hằng trong code (mặc định của `SYSTEM_SETTINGS`).
 * - `org`: admin đã đổi ở chính sách tổ chức.
 * - `skill`: skill xin chặt hơn trần tổ chức, được nhận.
 * - `clamped`: skill xin rộng hơn trần, bị kẹp về trần (xem `clamped[]`).
 * - `mode`: chế độ chạy (`sandbox`) siết thêm.
 */
export const ConstraintSource = z.enum(['default', 'org', 'skill', 'clamped', 'mode']);
export const ResolvedConstraints = z.object({
    limits: AiRunLimits,
    sources: z.record(AiRunLimitKey, ConstraintSource),
    clamped: z.array(z.object({ key: AiRunLimitKey, requested: z.number(), applied: z.number() })),
    /** Skill admin đã tắt cho tổ chức này. */
    disabledSkills: z.array(z.string()),
    /** Mức suy nghĩ đã gộp — APM-H-l. */
    reasoning: ResolvedReasoning,
    /** Giới hạn tần suất theo người — APM-H-o. `0` = tắt. Đếm trong RAM, một instance. */
    rateLimit: z.object({
        perUserPerMinute: z.number().int().min(0),
        perSkillPerUserPerHour: z.number().int().min(0),
    }),
    /** Ẩn danh tên/email thành viên trước khi gửi model — APM-H-o. Mặc định tắt. */
    redaction: z.boolean(),
    /** Agent phản biện — APM-CRIT-b. Tuỳ chọn để dấu vết cũ và fixture cũ vẫn đọc được. */
    critique: ResolvedCritique.optional(),
    /**
     * Trần lưu trữ của dấu vết — APM-H-ap. Không phải trần cho model (đó là
     * `limits.contextBudgetTokens`); skill không xin được, chỉ tổ chức đặt.
     * Tuỳ chọn vì dấu vết cũ không có.
     */
    trace: z.object({ maxResultChars: z.number().int().positive() }).optional(),
});
/**
 * Vì sao một tool call không chạy — ghi trên `tool_result.error` để đếm được
 * "lỗi tool theo loại" (`plan-harness.md` §3).
 *
 * - `unknown_tool`: tên không có trong registry (model bịa).
 * - `hidden`: tool có thật nhưng người bấm không có quyền — model được nói rõ
 *   để chuyển hướng thay vì tưởng gõ sai tên.
 * - `input`: tham số sai (Zod, luật tham số, hoặc tool ném lỗi 4xx) — thử lại được.
 * - `terminal`: tool hỏng vì hạ tầng — bị rút khỏi run, model không gọi lại.
 * - `timeout`: quá `toolTimeoutMs`.
 */
export const ToolDenyReason = z.enum([
    'unknown_tool',
    'hidden',
    'input',
    'terminal',
    'timeout',
    /** Chế độ `replay`: run gốc không gọi tool này với tham số này — APM-H-e. */
    'replay_miss',
]);
/** Trần nào bị chạm — bước `limit`. */
export const RunLimitKind = z.enum([
    'turns',
    'tool_calls',
    'input_tokens',
    'cost',
    'tool_timeout',
    'context',
    /** Gọi lặp một tool quá trần (`maxIdenticalToolCalls` / `maxCallsPerTool`) — APM-H-o. */
    'loop',
]);
/**
 * Vì sao một run kết thúc bằng **kết quả một phần** — APM-H-p. Chạm trần khi
 * model còn muốn gọi tool thì harness cho đúng **một lượt kết** chỉ có
 * `submit_result`; kết quả mang `partial: true` và lý do ở đây. Là **trường
 * tuỳ chọn thêm vào** `done`/`ai_suggestion`, không phải giá trị mới của trạng thái.
 */
export const AiStopReason = z.enum([
    'turn_capped',
    'tool_calls_capped',
    'token_capped',
    'cost_capped',
    'loop_capped',
]);
//# sourceMappingURL=ai-policy.js.map